/**
 * An applicant's draft answers, and the two shapes they become:
 *
 * - `normalize` — the answer as the SERVER stores it (`check_answer` in
 *   `questionnaires::graph`), which is what branch conditions read. Kept in step
 *   with Rust so the form shows the same path the server will judge.
 * - `toPayload` — what `QUESTIONNAIRE_SUBMITTED` sends for one question.
 *
 * `problemOf` is the client's early warning only: the server re-checks every
 * answer and is the authority.
 */
import type { Answer, Question } from './types';

export interface UploadedFile {
  id: string;
  name: string;
}

export type Draft =
  | string
  | boolean
  | { value: string; text: string }
  | { value: string[]; text: string }
  | { files: UploadedFile[] }
  | undefined;

export type Problem =
  | 'required'
  | 'format'
  | 'length'
  | 'range'
  | 'email'
  | 'phone'
  | 'number'
  | 'choiceText';

const TEXT_MAX = 5000;

function text(draft: Draft): string {
  return typeof draft === 'string' ? draft.trim() : '';
}

function picked(draft: Draft): { value: string; text: string } | null {
  if (typeof draft !== 'object' || draft === null || !('value' in draft)) return null;
  if (typeof draft.value !== 'string' || !draft.value) return null;
  return { value: draft.value, text: draft.text.trim() };
}

function pickedMany(draft: Draft): string[] {
  if (typeof draft !== 'object' || draft === null || !('value' in draft)) return [];
  return Array.isArray(draft.value) ? [...new Set(draft.value)] : [];
}

function files(draft: Draft): UploadedFile[] {
  return typeof draft === 'object' && draft !== null && 'files' in draft ? draft.files : [];
}

const asNumber = (s: string): number | null => {
  if (!s) return null;
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/** The server's stored shape, or null when unanswered. */
export function normalize(q: Question, draft: Draft): Answer | null {
  switch (q.element_type) {
    case 'consent':
      return draft === true ? { value: true } : null;
    case 'number': {
      const n = asNumber(text(draft));
      return n === null ? null : { value: n };
    }
    case 'dropdown':
    case 'single_choice': {
      const p = picked(draft);
      if (!p) return null;
      const asks = q.choices?.find((c) => c.value === p.value)?.requires_text_input;
      return asks ? { value: p.value, text: p.text } : { value: p.value };
    }
    case 'multiple_choice': {
      const many = pickedMany(draft);
      return many.length ? { value: many } : null;
    }
    case 'file_upload':
    case 'image': {
      const f = files(draft);
      return f.length ? { files: f.map((x) => x.id) } : null;
    }
    default: {
      const s = text(draft);
      return s ? { value: s } : null;
    }
  }
}

/** What to send for one question; `undefined` = leave it out. */
export function toPayload(q: Question, draft: Draft): unknown {
  const a = normalize(q, draft);
  if (!a) return undefined;
  switch (q.element_type) {
    case 'dropdown':
    case 'single_choice':
    case 'multiple_choice':
    case 'file_upload':
    case 'image':
      return a;
    default:
      return 'value' in a ? a.value : undefined;
  }
}

/** Mirrors `TextFormat` in Rust: letters in any script, Thai marks included. */
const LETTERS = /^[\p{L}\p{M} ]+$/u;
const ALNUM = /^[\p{L}\p{M}\p{N} ]+$/u;

function patternMatches(pattern: string, s: string): boolean {
  try {
    return new RegExp(`^(?:${pattern})$`, 'u').test(s);
  } catch {
    // A pattern JS cannot read: leave the judgement to the server.
    return true;
  }
}

export function problemOf(q: Question, draft: Draft): Problem | null {
  const a = normalize(q, draft);
  const et = q.element_type;
  if (!a) {
    if (et === 'number' && text(draft)) return 'number';
    if ((et === 'dropdown' || et === 'single_choice') && picked(draft)) return 'choiceText';
    return q.required ? 'required' : null;
  }
  const v = q.validation ?? {};
  const s = text(draft);
  switch (et) {
    case 'short_text':
    case 'long_text': {
      const n = [...s].length;
      if (n > TEXT_MAX || (v.min_length ?? 0) > n || (v.max_length != null && n > v.max_length))
        return 'length';
      if (et === 'short_text' && s.includes('\n')) return 'format';
      if (v.format === 'digits' && !/^\d+$/.test(s)) return 'format';
      if (v.format === 'letters' && !LETTERS.test(s)) return 'format';
      if (v.format === 'alphanumeric' && !ALNUM.test(s)) return 'format';
      if (v.format === 'regex' && v.pattern && !patternMatches(v.pattern, s)) return 'format';
      return null;
    }
    case 'email':
      return /^[^\s@]+@[^\s@]+\.[^\s@.]+$/.test(s) ? null : 'email';
    case 'phone':
      return /^\+[1-9]\d{6,14}$/.test(s) ? null : 'phone';
    case 'number': {
      const n = (a as { value: number }).value;
      if ((v.min != null && n < v.min) || (v.max != null && n > v.max)) return 'range';
      return null;
    }
    case 'dropdown':
    case 'single_choice': {
      const asks = q.choices?.find((c) => c.value === (a as { value: string }).value)
        ?.requires_text_input;
      return asks && !(a as { text?: string }).text ? 'choiceText' : null;
    }
    default:
      return null;
  }
}
